import classNames from "classnames";
import React, { ReactElement, useContext, useEffect, useRef } from "react";
import Button from "../button";
import { FilterModuleProps } from "./filter.types";
import { FilterContext } from "./util/filterContext";

const FilterModule = ({
  type,
  title: moduleTitle,
  name,
  input,
  clearBtnText = "Clear",
  showClearButton = true,
  isClearBtnEnabled,
  _privateProps,
  children,
}: FilterModuleProps): ReactElement => {
  const { isXsScreen } = useContext(FilterContext);
  const contentHeightXsRef = useRef<string>("0px");
  const [contentHeight, setContentHeight] = React.useState("auto");
  const contentRef = useRef<HTMLDivElement>(null);
  const [xsExpanded, setXsExpanded] = React.useState<boolean>(false);
  const clearBtnClasses = classNames("spark-filter-module__clear", {
    "spark-filter-module__clear--disabled": !isClearBtnEnabled,
  });

  const moduleClasses = classNames("spark-filter-module", {
    hide: _privateProps?.hide,
    "spark-panel": isXsScreen,
    "spark-panel--expand": isXsScreen,
    expanded: xsExpanded,
  });

  const headerClasses = classNames("spark-filter-module__header", {
    "spark-panel__header": isXsScreen,
  });

  const filterHeaderProps = {
    className: headerClasses,
    role: isXsScreen ? "heading" : undefined,
    tabIndex: isXsScreen ? 0 : undefined,
  };

  const bodyClasses = classNames("spark-filter-module__body", {
    "spark-panel__content": isXsScreen,
  });

  const onClearBtnClickHandle = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    _privateProps?.onClearFilterInternal?.(e, name);
  };

  useEffect(() => {
    if (isXsScreen) {
      setContentHeight(contentHeightXsRef.current);
    } else {
      setContentHeight("auto");
    }
  }, [isXsScreen]);

  const handleToggle = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!xsExpanded) {
      setXsExpanded(true);
      requestAnimationFrame(() => {
        console.log("requestAnimationFrame", contentRef.current?.scrollHeight);
        if (
          contentRef.current &&
          contentRef.current?.scrollHeight !== undefined
        ) {
          contentHeightXsRef.current = `${contentRef.current.scrollHeight}px`;
          setContentHeight(`${contentRef.current.scrollHeight}px`);
        }
      });
    } else {
      requestAnimationFrame(() => {
        setContentHeight("0px");
        contentHeightXsRef.current = "0px";
      });
      setTimeout(() => {
        setXsExpanded(false);
      }, 200);
    }
  };

  return (
    <div className={moduleClasses}>
      <div className="spark-filter-module__wrapper">
        <div
          {...filterHeaderProps}
          onClick={isXsScreen ? handleToggle : undefined}
        >
          <h4 className="spark-filter-module__title">{moduleTitle}</h4>
        </div>
        <div
          ref={contentRef}
          className="spark-animate-height"
          style={{ height: contentHeight, overflow: "hidden" }}
        >
          <div className={bodyClasses}>
            <div className="spark-filter-module__clear-container">
              {showClearButton ? (
                <Button
                  className={clearBtnClasses}
                  textOnly
                  onClick={onClearBtnClickHandle}
                  disabled={!isClearBtnEnabled}
                >
                  {clearBtnText}
                </Button>
              ) : null}
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModule;
